import React, {useState, useEffect, useRef} from 'react';
import {products as mockProducts} from '../utils/mockData';
import ProductGrid from '../components/concept-sort/ProductGrid';
import ConceptGrid from '../components/concept-sort/ConceptGrid';
import {Product, Concept} from '../utils/types';
import {Check} from 'lucide-react';
import {motion, AnimatePresence} from 'framer-motion';
import AppIcon from '../utils/AppIcon';
const productsConceptsData = require('../utils/products_concepts_link.json');

const ConceptSortPage: React.FC = () => {
    const [products, setProducts] = useState<Product[]>([]);
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [concepts, setConcepts] = useState<Concept[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSorting, setIsSorting] = useState<boolean>(false);
    const [sortSuccess, setSortSuccess] = useState<boolean>(false);
    const [sortingCompleted, setSortingCompleted] = useState<boolean>(false);
    const [allConcepts, setAllConcepts] = useState<Concept[]>([]);
    const [zoomProduct, setZoomProduct] = useState<Product | null>(null);
    const [zoomConcept, setZoomConcept] = useState<Concept | null>(null);
    
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (mockProducts.length > 0) {
            const firstProduct = mockProducts[0];

            if (firstProduct.concepts && firstProduct.concepts.length > 0) {
                console.log('First concept:', {
                    id: firstProduct.concepts[0].id,
                    name: firstProduct.concepts[0].name,
                    imageUrl: firstProduct.concepts[0].imageUrl ? 'Has image URL' : 'No image URL'
                });
            }
        }

        setProducts(mockProducts);

        const globalConcepts = productsConceptsData.concepts || [];
        const allConceptObjects = globalConcepts.map((concept: any) => ({
            id: concept.id,
            imageUrl: concept.imageUrl || `https://picsum.photos/seed/${concept.id}/300/300`,
            name: concept.name || `Concept ${concept.id.substring(0, 8)}`
        }));

        setAllConcepts(allConceptObjects);

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);

    const shuffleArray = (array: Concept[]): Concept[] => {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    };

    const handleProductSelect = (product: Product) => {
        setSelectedProduct(product);
        setIsLoading(true);
        setSortSuccess(false);
        setSortingCompleted(false);
        setZoomConcept(null);

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            setConcepts(allConcepts);
            setIsLoading(false);
        }, 500);
    };

    const handleSortConcepts = () => {
        if (!selectedProduct || isSorting || sortingCompleted) return;

        setZoomConcept(null);
        setIsSorting(true);
        setIsLoading(true);

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        setConcepts([]);

        timeoutRef.current = setTimeout(() => {
            if (selectedProduct.concepts) {
                const productConceptIds = new Set(
                    selectedProduct.concepts.map(concept => concept.id)
                );

                const filteredConcepts = allConcepts.filter(concept =>
                    productConceptIds.has(concept.id)
                );

                setConcepts(filteredConcepts);
                setIsLoading(false);

                timeoutRef.current = setTimeout(() => {
                    if (selectedProduct.alexOrder && selectedProduct.concepts) {
                        const conceptMap = new Map(
                            selectedProduct.concepts.map(concept => [concept.id, concept])
                        );

                        const sortedConcepts = selectedProduct.alexOrder
                            .map(id => conceptMap.get(id))
                            .filter((concept): concept is Concept => concept !== undefined);

                        setConcepts(sortedConcepts);

                        timeoutRef.current = setTimeout(() => {
                            setIsSorting(false);
                            setSortSuccess(true);
                            setSortingCompleted(true);
                        }, 1200);
                    } else {
                        setIsSorting(false);
                    }
                }, 2000);
            }
        }, 800);
    };

    return (
        <div className="min-h-screen bg-gray-50 p-4">
            <div className="max-w-7xl mx-auto">

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <motion.div
                        className="bg-white rounded-lg shadow-lg p-4"
                        initial={{opacity: 0, x: -50}}
                        animate={{opacity: 1, x: 0}}
                        transition={{duration: 0.5, delay: 0.1}}
                    >
                        <div className="flex justify-between items-center mb-4 h-[56px]">
                            <h2 className="text-xl font-semibold ">Product Images</h2>
                        </div>
                        <ProductGrid
                            products={products}
                            selectedProduct={selectedProduct}
                            onProductSelect={handleProductSelect}
                            zoomProduct={zoomProduct}
                            setZoomProduct={setZoomProduct}
                        />
                    </motion.div>

                    <motion.div
                        className="bg-white rounded-lg shadow-lg p-4"
                        initial={{opacity: 0, x: 50}}
                        animate={{opacity: 1, x: 0}}
                        transition={{duration: 0.5, delay: 0.2}}
                    >
                        <div className="flex justify-between items-center mb-4 h-[56px]">
                            <div className="flex justify-between items-center gap-1">
                                <h2 className="text-xl font-semibold">Concept Matching Images</h2>
                                {!!selectedProduct && !!concepts?.length && 
                                    <span className="mt-1 text-black bg-blue-200 border border-solid border-[#C6DBE9] px-2 text-sm rounded-full h-[20px]">{concepts?.length}</span>
                                }
                            </div>
                            <AnimatePresence mode="wait">
                                {sortSuccess ? (
                                    <motion.div
                                        key="success"
                                        initial={{opacity: 0, scale: 0.8}}
                                        animate={{opacity: 1, scale: 1}}
                                        exit={{opacity: 0, scale: 0.8}}
                                        className="px-4 py-2 bg-green-600 text-white rounded-full flex items-center gap-2"
                                    >
                                        <Check className="h-5 w-5"/>
                                        <span>Successfully sorted & filtered!</span>
                                    </motion.div>
                                ) : (
                                    <motion.button
                                        key="sort-button"
                                        onClick={handleSortConcepts}
                                        disabled={!selectedProduct || isLoading || isSorting || sortingCompleted}
                                        className={`px-4 py-2 bg-opacity-20 rounded-full flex items-center gap-2 ${sortingCompleted ? 'opacity-50 cursor-not-allowed' : 'disabled:opacity-50'}`}
                                        whileHover={!sortingCompleted ? {scale: 1.05} : {}}
                                        whileTap={!sortingCompleted ? {scale: 0.95} : {}}
                                        initial={{opacity: 0}}
                                        animate={{opacity: 1}}
                                        exit={{opacity: 0}}
                                    >
                                        {isSorting ? (
                                            <>
                                                <svg className="animate-spin h-4 w-4 text-blue-500"
                                                     xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10"
                                                            stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor"
                                                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                <span>
                                                    {isLoading ? "Filtering concepts..." : "Sorting concepts..."}
                                                </span>
                                            </>
                                        ) : (
                                            <>
                                                <i className="bg-blue-500 text-white rounded-full p-2">
                                                    <AppIcon name="auto_fix_ico_g" size={24}/>
                                                </i>

                                                <span
                                                    className="text-blue-500 text-sm py-1 rounded-full">Sort & filter the concepts</span>
                                            </>
                                        )}
                                    </motion.button>
                                )}
                            </AnimatePresence>
                        </div>
                        <ConceptGrid
                            concepts={concepts}
                            isLoading={isLoading}
                            productSelected={!!selectedProduct}
                            isSorting={isSorting && !isLoading}
                            zoomConcept={zoomConcept}
                            setZoomConcept={setZoomConcept}
                        />
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

export default ConceptSortPage;